import { Settings } from '../Settings'
import PasswordInput from "../Components/Inputs/PasswordInput";
import EmailInput from "../Components/Inputs/EmailInput";
import SelectInput from "../Components/Inputs/SelectInput";
import TextInput from "../Components/Inputs/TextInput";
import {Fragment} from "react";
import queryString from "query-string";

export default class Globals {
    static addQueryParam = (url, name, value) => {
        if (value === undefined || value === null || value === '') {
            return url;
        }
        var fullUrl;
        if (url.includes("?")) {
            fullUrl = url + "&" + name + "=" + value;
        } else {
            fullUrl = url + "?" + name + "=" + value;
        }
        return fullUrl;
    }
}

const getCartParam = (url) => {
    const parsed = queryString.parse(window.location.search.toLowerCase());
    const external = parsed.bl_cartexternalid ?? parsed.cartexternalid
    const source = parsed.bl_cartsourceexternalid ?? parsed.cartsourceexternalid
    const uid = parsed.bl_cartuid ?? parsed.cartuid
    if (external)
        return `cartexternalid=${external}`
    if (source)
        return `cartsourceexternalid=${source}`
    else
        return `cartuid=${uid}`
}

export const sanitizeCustomerName = (name) => {
    if (!name) return ''
    // Keep only alphanumeric characters (a-z, A-Z, 0-9) and spaces, replace everything else with spaces
    return String(name).replace(/[^a-zA-Z0-9 ]/g, ' ')
}

export const copyTextToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(String(text))
        return true
    } catch (e) {
        try {
            const textArea = document.createElement('textarea')
            textArea.value = String(text)
            textArea.style.position = 'fixed'
            textArea.style.top = '-1000px'
            document.body.appendChild(textArea)
            textArea.focus()
            textArea.select()
            const successful = document.execCommand('copy')
            document.body.removeChild(textArea)
            return successful
        } catch {
            return false
        }
    }
}

export const mapUrlToOldSnippet = (url) => {
    const parsed = queryString.parse(window.location.search.toLowerCase());
    const baseUrl = window.location.origin + window.location.pathname
    var newUrl = baseUrl.replace('new.bloyal', '.bloyal')
    const sessionKey = parsed.bl_sk ?? parsed.sessionkey
    newUrl = `${newUrl}?sessionKey=${sessionKey}&${getCartParam(url)}&logindomain=${parsed.bl_logindomain ?? parsed.logindomain ?? ""}&cashiercode=${parsed.bl_cashiercode ?? ""}&ismanager=no&bl_rurl=${parsed.bl_rurl ?? ""}`
    return newUrl
}

export const isAlertResumeRoute = (href = window.location.href) => {
    try {
        return /\/POS\/AlertResume/i.test(new URL(href).pathname)
    } catch {
        return /\/POS\/AlertResume/i.test(href)
    }
}

export const isSnippetRouteUrl = (href = window.location.href) => {
    try {
        const url = new URL(href, window.location.origin)
        return url.origin === window.location.origin && /^\/POS(\/|$)/i.test(url.pathname)
    } catch {
        return false
    }
}

export const appendBlCloseParam = (href = window.location.href) => {
    try {
        const url = new URL(href)
        url.searchParams.set('bl_close', 'true')
        return url.toString()
    } catch {
        const separator = href.includes('?') ? '&' : '?'
        return `${href}${separator}bl_close=true`
    }
}

export const closeWindow = () => {
    window?.close()
    // The window.close fallback reloads this page with bl_close; don't do it twice or the page loops.
    if ((getQueryString(window.location.href, 'bl_close') ?? '').toLowerCase() === 'true') {
        return
    }
    window.location.href = appendBlCloseParam(window.location.href)
}

export const clearResumeAlertProcessing = (href = window.location.href) => {
    try {
        const url = new URL(href)
        url.searchParams.delete('bl_resumeAlerts')
        url.searchParams.delete('resumeAlerts')
        window.history.replaceState({}, '', url.toString())
    } catch (e) {
        console.log(e)
    }
}

export const getAlertResumeGuardKey = ({ sessionKey, cartUid, cartExternalId, cartSourceExternalId }) => {
    const cartId = cartUid || cartExternalId || cartSourceExternalId || 'cart';
    return `alertResumeDone:${sessionKey}:${cartId}`;
}

export const clearAlertResumeGuard = (params = readCartSnippetParams()) => {
    try {
        sessionStorage.removeItem(getAlertResumeGuardKey(params));
    } catch {
        // ignore session storage failures
    }
}

export const completeAlertSnippet = (returnUrl = null, href = window.location.href) => {
    const resolvedReturnUrl = returnUrl || getReturnUrl(href)

    if (resolvedReturnUrl && isAlertResumeRoute(resolvedReturnUrl)) {
        clearAlertResumeGuard(readCartSnippetParams(resolvedReturnUrl))
    }

    if (shouldResumeAlertProcessing(href)) {
        clearResumeAlertProcessing(href)
    }

    onSnippetComplete(resolvedReturnUrl)
}

export const finishAlertSnippetClose = ({
    returnUrl = null,
    href = window.location.href,
    acknowledgeAlert = null,
}) => {
    if (acknowledgeAlert) {
        Promise.resolve(acknowledgeAlert()).catch((error) => console.log(error))
    }

    let resolvedReturnUrl = returnUrl || getReturnUrl(href)
    const isChainedAlert = !!(resolvedReturnUrl && isAlertResumeRoute(resolvedReturnUrl))
    if (isAlertSnippet(href) && !isChainedAlert) {
        resolvedReturnUrl = ''
    }

    completeAlertSnippet(resolvedReturnUrl, href)
}

export const getAlertChainDestinationUrl = (returnUrl, href = window.location.href) => {
    const resolvedReturnUrl = returnUrl || getFinalReturnUrl(href);

    if (resolvedReturnUrl) {
        // The chain carries the origin snippet url; the destination is the return url it was opened with.
        const externalReturnUrl = getReturnUrl(resolvedReturnUrl);
        if (externalReturnUrl) {
            return externalReturnUrl
        }

        // Already an external return url — go there.
        if (!isSnippetRouteUrl(resolvedReturnUrl)) {
            return resolvedReturnUrl
        }
    }

    // The origin snippet url can arrive flattened, its own params spilled onto this url. The resume
    // route never carries a bl_rurl of its own, so one found here is the return url that leaked out.
    const ownReturnUrl = getReturnUrl(href);
    return ownReturnUrl && !isSnippetRouteUrl(ownReturnUrl) ? ownReturnUrl : ''
}

export const completeAlertChain = (returnUrl) => {
    const destinationUrl = getAlertChainDestinationUrl(returnUrl);
    if (destinationUrl) {
        onSnippetComplete(destinationUrl)
        return
    }
    if (returnUrl || getFinalReturnUrl() || isAlertResumeRoute()) {
        closeWindow()
        return
    }
    if (shouldResumeAlertProcessing()) {
        clearResumeAlertProcessing()
        return
    }
    onSnippetComplete(returnUrl)
}

const isNavigableUrl = (url) => /^[a-z][a-z0-9+.-]*:\/\//i.test(url) || url.startsWith('/')

// Callers pass either a decoded url (getReturnUrl) or a raw encoded query value (getQueryString).
// Decoding an already-decoded url flattens the return urls nested inside it, so decode only until
// the value is navigable.
export const decodeReturnUrl = (returnUrl) => {
    let url = returnUrl
    for (let i = 0; i < 3 && !isNavigableUrl(url); i++) {
        let decoded
        try {
            decoded = decodeURIComponent(url)
        } catch {
            break
        }
        if (decoded === url) break
        url = decoded
    }
    return url
}

export const onSnippetComplete = (returnUrl, params = {}) => {
    console.log('onSnippetComplete', returnUrl, params)
    
    // Build query parameters from params object
    const queryParams = [];
    if (params.customerUid) {
        queryParams.push(`bl_customerUid=${encodeURIComponent(params.customerUid)}`);
    }
    if (params.customerPhone) {
        queryParams.push(`bl_customerPhone=${encodeURIComponent(params.customerPhone)}`);
    }
    if (params.couponCode) {
        queryParams.push(`bl_couponCode=${encodeURIComponent(params.couponCode)}`);
    }

    const queryString = queryParams.length > 0 ? queryParams.join('&') : '';

    if (!returnUrl) {
        if (shouldResumeAlertProcessing()) {
            clearResumeAlertProcessing()
        }
        window?.close()
        try {
            const url = new URL(window.location.href)
            if (params.customerUid) url.searchParams.set('bl_customerUid', params.customerUid)
            if (params.customerPhone) url.searchParams.set('bl_customerPhone', params.customerPhone)
            if (params.couponCode) url.searchParams.set('bl_couponCode', params.couponCode)
            url.searchParams.set('bl_close', 'true')
            window.location.href = url.toString()
        } catch {
            closeWindow()
        }
        return;
    }
    
    try {
        let url = decodeReturnUrl(returnUrl);

        // Append query parameters to returnUrl
        if (queryString) {
            const separator = url.includes('?') ? '&' : '?';
            url = `${url}${separator}${queryString}`;
        }

        window.location.href = url;
    } catch (e) {
        console.log(e)
    }
}

// Hosts spell these params inconsistently (bl_rurl, bL_rurl, ReturnUrl, returnURL), so match on the
// lowercased name rather than the exact casing. Names are tried in order of precedence.
const getUrlParamValue = (href, names) => {
    try {
        const url = new URL(href)
        for (const name of names) {
            for (const [key, value] of url.searchParams.entries()) {
                if (key.toLowerCase() === name && value) {
                    return value
                }
            }
        }
        return ''
    } catch {
        for (const name of names) {
            const raw = getQueryString(href, name)
            if (!raw) continue
            try {
                return decodeURIComponent(raw)
            } catch {
                return raw
            }
        }
        return ''
    }
}

export const getReturnUrl = (href = window.location.href) => {
    return getUrlParamValue(href, ['bl_rurl', 'returnurl'])
}

export const isAlertSnippet = (href = window.location.href) => {
    return !!(getQueryString(href, 'bl_alertUid') ?? getQueryString(href, 'alertUid'))
}

// Snippet setting: bl_showDeclineButton / showDeclineButton (default true). When false the alert
// snippets show a single Close button that records Dismissed.
export const readShowDeclineButton = (href = window.location.href) => {
    const value = getQueryString(href, 'bl_showDeclineButton') ?? getQueryString(href, 'showDeclineButton')
    if (value === undefined || value === null || value === '') {
        return true
    }
    const normalized = String(value).toLowerCase()
    return !(normalized === 'false' || normalized === '0' || normalized === 'no')
}

export const readCartSnippetParams = (href = window.location.href) => ({
    sessionKey: getQueryString(href, 'bl_sk') ?? getQueryString(href, 'SessionKey') ?? getQueryString(href, 'sessionKey') ?? '',
    loginDomain: getQueryString(href, 'bl_LoginDomain') ?? getQueryString(href, 'LoginDomain') ?? getQueryString(href, 'loginDomain') ?? '',
    cartUid: getQueryString(href, 'bL_CartUid') ?? getQueryString(href, 'CartUid') ?? getQueryString(href, 'cartUid') ?? '',
    cartExternalId: getQueryString(href, 'bl_CartExternalId') ?? getQueryString(href, 'cartExternalId') ?? '',
    cartSourceExternalId: getQueryString(href, 'bl_CartSourceExternalId') ?? getQueryString(href, 'CartSourceExternalId') ?? getQueryString(href, 'cartSourceExternalId') ?? '',
    returnUrl: getReturnUrl(href),
})

export const getFinalReturnUrl = (href = window.location.href) => {
    return getUrlParamValue(href, ['bl_finalrurl', 'finalreturnurl'])
}

export const getOriginSnippetUrl = (href = window.location.href) => {
    return getFinalReturnUrl(href) || (!isAlertResumeRoute(href) ? href : '');
}

export const buildAlertResumeRouteUrl = (href = window.location.href, originSnippetUrl = null) => {
    const params = readCartSnippetParams(href);
    const resolvedOriginUrl = originSnippetUrl ?? getOriginSnippetUrl(href);

    try {
        const url = new URL(`${window.location.origin}/POS/AlertResume`);
        if (params.sessionKey) url.searchParams.set('sessionKey', params.sessionKey);
        if (params.loginDomain) url.searchParams.set('loginDomain', params.loginDomain);
        if (params.cartUid) url.searchParams.set('cartUid', params.cartUid);
        if (params.cartExternalId) url.searchParams.set('cartExternalId', params.cartExternalId);
        if (params.cartSourceExternalId) url.searchParams.set('cartSourceExternalId', params.cartSourceExternalId);
        if (resolvedOriginUrl) url.searchParams.set('bl_finalRurl', resolvedOriginUrl);

        const cashierCode = getQueryString(href, 'bl_cashiercode') ?? getQueryString(href, 'cashiercode');
        if (cashierCode) url.searchParams.set('cashiercode', cashierCode);

        const isManager = getQueryString(href, 'bl_ismanager') ?? getQueryString(href, 'ismanager');
        if (isManager) url.searchParams.set('ismanager', isManager);

        return url.toString();
    } catch {
        let routeUrl = `${window.location.origin}/POS/AlertResume?sessionKey=${encodeURIComponent(params.sessionKey)}`;
        if (params.loginDomain) routeUrl += `&loginDomain=${encodeURIComponent(params.loginDomain)}`;
        if (params.cartUid) routeUrl += `&cartUid=${encodeURIComponent(params.cartUid)}`;
        if (resolvedOriginUrl) routeUrl += `&bl_finalRurl=${encodeURIComponent(resolvedOriginUrl)}`;
        return routeUrl;
    }
}

export const shouldResumeAlertProcessing = (href = window.location.href) => {
    const value = getQueryString(href, 'bl_resumeAlerts') ?? getQueryString(href, 'resumeAlerts')
    return value === '1' || value === 'true'
}

/** @deprecated use buildAlertResumeRouteUrl */
export const buildAlertResumeUrl = (href = window.location.href, originSnippetUrl = null) => {
    if (shouldResumeAlertProcessing(href) && !isAlertResumeRoute(href)) {
        return buildAlertResumeRouteUrl(href, originSnippetUrl ?? href);
    }
    return buildAlertResumeRouteUrl(href, originSnippetUrl);
}

const setUrlQueryParam = (url, name, value) => {
    try {
        const parsed = new URL(url, window.location.origin)
        parsed.searchParams.set(name, value)
        return parsed.toString()
    } catch {
        return Globals.addQueryParam(url, name, encodeURIComponent(value))
    }
}

const syncAlertUrlToResumeContext = (alertUrl, resumeUrl) => {
    try {
        const resume = new URL(resumeUrl)
        const alert = new URL(alertUrl, window.location.origin)
        const sessionKey = resume.searchParams.get('sessionKey')
            ?? resume.searchParams.get('sessionkey')
            ?? resume.searchParams.get('bl_sk')
        const loginDomain = resume.searchParams.get('loginDomain')
            ?? resume.searchParams.get('logindomain')
            ?? resume.searchParams.get('bl_LoginDomain')
        const cartUid = resume.searchParams.get('cartUid')
            ?? resume.searchParams.get('cartuid')
            ?? resume.searchParams.get('bl_CartUid')
        const cartExternalId = resume.searchParams.get('cartExternalId')
            ?? resume.searchParams.get('cartexternalid')
        const cartSourceExternalId = resume.searchParams.get('cartSourceExternalId')
            ?? resume.searchParams.get('cartsourceexternalid')

        if (sessionKey) {
            alert.searchParams.set('sessionKey', sessionKey)
            alert.searchParams.delete('bl_sk')
        }
        if (loginDomain) {
            alert.searchParams.set('loginDomain', loginDomain)
            alert.searchParams.delete('bl_LoginDomain')
        }
        if (cartUid) {
            alert.searchParams.set('cartUid', cartUid)
            alert.searchParams.delete('bl_CartUid')
        }
        if (cartExternalId) {
            alert.searchParams.set('cartExternalId', cartExternalId)
        }
        if (cartSourceExternalId) {
            alert.searchParams.set('cartSourceExternalId', cartSourceExternalId)
        }
        return alert.toString()
    } catch {
        return alertUrl
    }
}

const appendResumeUrlToAlert = (alertUrl, resumeUrl) => {
    if (!resumeUrl) return alertUrl
    try {
        const parsed = new URL(alertUrl, window.location.origin)
        parsed.searchParams.delete('bl_rurl')
        parsed.searchParams.delete('ReturnUrl')
        parsed.searchParams.delete('returnURL')
        parsed.searchParams.set('bl_rurl', resumeUrl)
        return parsed.toString()
    } catch {
        let url = alertUrl.replace(/([?&])(bl_rurl|ReturnUrl|returnURL)=[^&]*/gi, '$1').replace(/[?&]$/, '')
        return Globals.addQueryParam(url, 'bl_rurl', encodeURIComponent(resumeUrl))
    }
}

export const handleCartAlerts = (cartResponse, returnUrl, onComplete, options = {}) => {
    const complete = onComplete ?? (() => completeAlertChain(returnUrl));
    const { resumeUrl = null } = options
    const alertCount = cartResponse?.LoyaltySummary?.Alerts?.length ?? 0
    console.log('[handleCartAlerts]', { alertCount, returnUrl, resumeUrl, cartResponse })
    if (!cartResponse?.LoyaltySummary?.Alerts?.length) {
        console.log('[handleCartAlerts] no alerts — calling complete')
        complete();
        return false;
    }
    const alert = cartResponse.LoyaltySummary.Alerts[0];
    let url = alert?.SnippetUrl;
    if (!url) {
        console.log('[handleCartAlerts] alert has no SnippetUrl — calling onSnippetComplete')
        complete();
        return false;
    }
    if (resumeUrl) {
        url = syncAlertUrlToResumeContext(url, resumeUrl)
        url = appendResumeUrlToAlert(url, resumeUrl)
    }
    console.log('[handleCartAlerts] redirecting to alert snippet', url)
    window.location.assign(url);
    return true;
};

export const completeSnippetOrChainAlerts = async ({
    refetchCart,
    returnUrl,
    onComplete,
    acknowledgeAlert,
    resumeUrl,
}) => {
    const complete = onComplete ?? (() => onSnippetComplete(returnUrl))

    if (isAlertSnippet()) {
        if (acknowledgeAlert) {
            await acknowledgeAlert()
        }
        completeAlertSnippet(returnUrl)
        return
    }

    let cartData
    try {
        cartData = await refetchCart()
    } catch (error) {
        console.log(error)
        complete()
        return
    }

    const cartResponse = cartData?.data ?? cartData
    clearAlertResumeGuard(readCartSnippetParams())
    const effectiveResumeUrl = resumeUrl ?? buildAlertResumeRouteUrl(window.location.href, window.location.href)
    const redirected = handleCartAlerts(cartResponse, returnUrl, complete, { resumeUrl: effectiveResumeUrl })
    if (!redirected) {
        complete()
    }
}

String.prototype.isEmpty = function isEmpty() {
    return (!this || this.length === 0)
}

export const getQueryString = (url, key) => {
    var vars = [],
        hash;
    var hashes = (url.slice(url.indexOf('?') + 1).split('&'));

    for (var i = 0; i < hashes.length; i++) {
        hash = hashes[i].split('=');
        vars.push(hash[0].toLowerCase());
        vars[hash[0].toLowerCase()] = hash[1];
    }
    return vars[key.toLowerCase()];
}

export const hasCartIdentifier = (uid, externalId, sourceExternalId) => {
    return uid || externalId || sourceExternalId
}

export const checkBirthDate = (date) => {
    if (!date)
        return true
    
    const dateRegex = /^(?:(?:31(\/)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\/)(?:0?[13-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29(\/)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)\d{2})$/
    return dateRegex.test(date)
}

export const getDatePattern = () => {
    return "/(0\d{1}|1[0-2])\/([0-2]\d{1}|3[0-1])\/(19|20)\d{2}/"
}

export const getQueryParam = (name, value) => {
    if (!value)
        return ""
    
    return `&${name}=${value}`
}

export const checkValidDate = (date) => {
    if (!date)
        return true;
    // your desired pattern
    var pattern = /(\d{1}|0\d{1}|1[0-2])\/(\d|[0-2]\d{1}|3[0-1])\/(19|20)(\d{2})/
    var m = date.match(pattern);
    if (!m)
        return false;
    var d = new Date(date);
    // Now let's ensure that the date is not out of index


    if (d.getMonth()+1 == parseInt(m[1], 10) && d.getDate() ==parseInt(m[2], 10)) {
        return true;
    }
    return false;
}

export let languageCode = 'en-US'

export const updateLanguageCode = (code) => {
    languageCode = code
}

export const formatDate = (inputDate) => {
    if (inputDate === undefined || inputDate === null)
        return null

    let options = {year: "numeric", month: "2-digit", day: "2-digit"}
    if (inputDate.includes('T')) {
        let date = new Date(inputDate);
        // @ts-ignore
        return date.toLocaleDateString(languageCode, options);
    }

    var date = new Date(inputDate.replace(/-/g, '\/'));
    if (!isNaN(date.getTime())) {
        // @ts-ignore
        return date.toLocaleDateString(languageCode, options)
    }

    return inputDate.toLocaleString();
}

export const getFullDate = (date) => {
    var fullDate = date.includes('T') ? new Date(date) : new Date(date.replace(/-/g, '\/'))
    return fullDate
}

export const mapServiceUrls = (urls) => {
    if (urls === undefined || urls === null)
        return undefined;

    const definedUrls = { ...Settings.ServiceUrls };
    const merged = {
        ...urls,
        ...definedUrls
    };
    try {
        const override = sessionStorage.getItem('bL_customLoyaltyEngineUrl');
        if (override && override.trim() !== '')
            merged.LoyaltyEngineApiUrl = override;
    } catch (e) {
        // ignore session storage failures
    }
    return merged;
};

export const isNullOrUndefined = (value) => {
    return value === undefined || value === null;
    
    
}

export const getSessionLoginDomain = (window) => {
    try {
        if (window === undefined)
            return ''
        
        return window.sessionStorage.getItem('blLoginDomain')
    } catch (e) {
        console.log('Unable to access session storage.')
        return ''
    }
}

export const setSessionLoginDomain = (domain) => {
    try {
        sessionStorage.setItem('blLoginDomain', domain)
    } catch (e) {
        
    }
}

export const safeSetSessionStorage = (key, value) => {
    try {
        sessionStorage.setItem(key, value)
    } catch (e) {

    }
}

export const getDisplayValue = (text) => {
    if (text === null)
        return '';


    let displayString = '';
    let lastChar = '\0'
    Array.from(text).forEach((ch) => {
        if (displayString.length !== 0 && ((ch >= 'A') && (ch <= 'Z')) && (((lastChar < 'A') || (lastChar > 'Z')) && (lastChar != ' ')))
            displayString += ' ';

        displayString += ch;
        lastChar = ch;
    })

    return displayString;
}

export const areAnyLoading = (statuses) => {
    let found = false
    statuses.forEach(status => {
        if (status === 'loading')
            found = true
    })
    return found
}